import {
  DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { LocalizedString } from '../localized-string.js';
import { VersionRange } from '../../core/version-range.js';

export class CapacityRoom extends DataModel {
  static readonly TYPE_ID: string = 'c4057061a22448baa9df1befdbfeb09f';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly buildingId: string,
    public readonly name: string,
    public readonly backgroundImage: string | null,
    public readonly publicName: LocalizedString | null = null,
  ) {
    super();
  }

  get $typeId(): string {
    return CapacityRoom.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(CapacityRoom.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, data: SerializedData): CapacityRoom {
    return new CapacityRoom(
        MapUtils.get<string>(data, 'uniqueId'),
        MapUtils.get<string>(data, 'buildingId'),
        MapUtils.get<string>(data, 'name'),
        MapUtils.getOrNull<string>(data, 'backgroundImage'),
        context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(data, 'publicName')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'buildingId': this.buildingId,
      'name': this.name,
      'backgroundImage': this.backgroundImage,
      'publicName': context.toDataOrNull(this.publicName),
    };
  }

  clone(): this {
    return new CapacityRoom(
        this.uniqueId,
        this.buildingId,
        this.name,
        this.backgroundImage,
        this.publicName?.clone() ?? null,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.buildingId === this.buildingId &&
        other.name === this.name &&
        other.backgroundImage === this.backgroundImage &&
        isSameData(other.publicName, this.publicName);
  }
}

export class CapacityRoomBuilder extends DataModelBuilder<CapacityRoom> {
  build(context: SerializationContext, map: SerializedData): CapacityRoom {
    return CapacityRoom.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(CapacityRoom.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(CapacityRoom.SCHEMA_VERSION)) {
      return null;
    }

    let version = versionRange.to;

    // In this version we added the `publicName` field of type `LocalizedString`.
    if(version < 2) {
      map['publicName'] ??= null;
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
